import React, { useCallback, useEffect, useRef, useMemo } from 'react';
import './LogicMapper.css';
import { RecipeGate, WidgetType } from '@kemu-io/kemu-core/types';
import { Connection, ConnectionEstablishedParams, ConnectionMovedParams } from '@jsplumb/community/types/core';
import { NoteWidgetState } from '@kemu-io/kemu-core/widgets/note/index.js';
import { useSelector, useDispatch } from 'react-redux';
import { removePrivateProperties , createWidgetPortIdentifier, decodeWidgetPortIdentifier } from '@kemu-io/kemu-core/common/utils';
import { FolderPathInfo, selectCurrentRecipeType, selectVisibleGroup } from '../Workspace/workspaceSlice';
import {
	selectCurrentWidgets,
	connectGates as addChildGate,
	disconnectGate as removeChildGate,
	setGatePosition,
	selectSelectedItems,
	selectRenderCounter,
} from './logicMapperSlice.ts';
import { getWidgetCanvasInfo, isGateAChildAtPort, getWidgetState } from '@src/app/recipe/utils';
import {
	StatelessWidgetsMap,
	JsPlumbBeforeDropEvent,
	PlumbDragEvent,
	KemuConnectionData,
} from '@src/types/core_t.ts';
import CanvasGateWrapper from '@components/canvasGate/canvasGateWrapper';
import { decodeDomId } from '@common/utils';
import Logger from '@common/logger';

import { LOGIC_MAPPER_CANVAS_CLASS } from '@common/constants';
import VirtualPortManager from '@components/virtualPort/virtualPortManager';
import useDragSelection from '@common/hooks/useDragSelection';
import { useLogicMapperContext } from '@common/context/LogicMapperContext/LogicMapperContext.tsx';
import useGetWidgetsMap from '@hooks/useGetWidgetsMap.ts';



const logger = Logger('logicMapper');
interface Props {
	/** the id of the block in the recipe */
	thingRecipeId: string;
	/** id of the thing in the database */
	thingDbId: string;
	/** thing's version number */
	thingVersion: string;
	/** the id of the recipe in the pool */
	recipeId: string;
	containerEl: HTMLDivElement | null;
}


/** 
 * SUPER important equality function to prevent re-rendering
 * every time a gate connection changes.
 * It extracts the gate's 'children' property that changes when a new connection is made/removed
 * the remaining object and compares the resulting strings
 **/
const ignoreCanvasUpdates = (current: StatelessWidgetsMap, prev: StatelessWidgetsMap) => {
	const reduceFun = (instance: StatelessWidgetsMap, map: RecipeGate, gateId: string) => {
		// NOTE: We are currently NOT ignoring changes to the 'children' property; this means
		// every time a new connection is made, ALL the widgets will be re-rendered.
		// The only way to avoid this is to read the children property directly from the recipe
		// pool in the 'connectGates' and 'disconnectGates'. However, I decided to add
		// a React.memo in the `canvasGateWrapper` instead, to prevent re-rendering of the canvas element itself.
		// Although the wrapper will be re-rendered on every connection, its computation cost is low enough to 
		// allow us to get away with it for now. If that changes, consider implementing the above mentioned alternative.


		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { canvas, ...rest } = instance[gateId];
		const cleanedProps = removePrivateProperties(rest);
		return { ...map, [gateId]: { ...cleanedProps } };
	};

	const prevFiltered = Object.keys(prev).reduce(reduceFun.bind(null, prev), {} as RecipeGate);
	const currentFiltered = Object.keys(current).reduce(reduceFun.bind(null, current), {} as RecipeGate);
	const areEqual = JSON.stringify(prevFiltered) === JSON.stringify(currentFiltered);
	return areEqual;
};

type SourceInfo = {
	id: string;
	type: string;
}

/**
 * Adds a class to the canvas container
 * NOTE: This is currently a workaround to avoid re-rendering all the canvas and children
 * elements. We manipulate the container directly using vanilla (ugh...)
 */
 const setCanvasContainerClass = (sources: SourceInfo[], remove=false) => {
	const canvasContainer = document.querySelector(`.${LOGIC_MAPPER_CANVAS_CLASS}`);
	if (canvasContainer) {
		// Remove any previous source type classes
		canvasContainer.classList.forEach(className => {
			if (className.match(/type-\d+/)) {
				canvasContainer.classList.remove(className);
			}
		});

		sources.forEach(source => {
			if (!remove) {
				canvasContainer.classList.add(source.type);
				canvasContainer.setAttribute('data-drag-source', source.id);
			} else {
				canvasContainer.classList.remove(source.type);
				canvasContainer.removeAttribute('data-drag-source');
			}
		});
	}
};

/**
 * Returns the origin endpoint types from the event
 */
const getSourceEndpointNames = (conn: Connection): null | SourceInfo[] => {
	if (conn?.endpoints && conn?.endpoints.length) {
		const classes = conn.endpoints[0].cssClass.split(' ');
		const types = classes.filter(className => className.startsWith('type-'));
		// Always return the last type, since it is the most specific one
		return types.length ? [{
			type: types[types.length - 1],
			id: conn.endpoints[0].elementId
		}] : null;
		// return types.map(type => ({
		// 	type,
		// 	id: conn.endpoints[0].elementId
		// }));
		// return types.length ? {
		// 	type,
		// 	id: conn.endpoints[0].elementId
		// } : null;
	}

	return null;
};


const LogicMapper = (props: Props): React.ReactNode => {
	const dispatch = useDispatch();
	const visibleGroup = useSelector(selectVisibleGroup);
	const { drawPortsAndConnections, jsPlumb: plumbInstance, cleanupAllVirtualPorts, removePortsAndConnections, setDrawingStatus } = useLogicMapperContext();
	const widgetsInRecipe = useSelector(selectCurrentWidgets, ignoreCanvasUpdates);
	const totalWidgets = useMemo(() => {
		return Object.keys(widgetsInRecipe).join('_');
	}, [widgetsInRecipe]);
	const getWidgetsMap = useGetWidgetsMap();
	// const lastDrawnWidgetsRef = useRef<StatelessWidgetsMap>({});
	const { recipeId, thingRecipeId, thingDbId, thingVersion } = props;
	// const getWidgetState = useGetWidgetState(recipeId, thingRecipeId);

	// const logicMapperSettings = useSelector(selectLogicMapperSettings);
	const currentRecipeType = useSelector(selectCurrentRecipeType);
	// const virtualPortsRef = useRef<VirtualPortsRef | null>(null);
	// Used to force the logic mapper to re-build connections (e.g., after exiting execution mode)
	const forceReRender = useSelector(selectRenderCounter);
	// A map of widgets that have already painted their ports
	// const paintedPortsRef = useRef<{visibleGroup: FolderPathInfo | null, widgets: Record<string, boolean>}>({ widgets: {}, visibleGroup });
	// const { jspInstance: plumbInstance } = usePlumb();
	// if(!plumbInstance) { throw new Error(`Plumb library has not be initialized`); }

	useDragSelection(props.containerEl, plumbInstance, visibleGroup?.groupId);
	const selectedWidgets = useSelector(selectSelectedItems);
	const previousSelectedWidgetsRef = useRef<typeof selectedWidgets>([]);
	// const gatesRef = useRef<StatelessWidgetsMap>({});

	// Contains a list of the visible gates
	const gatesToShow: StatelessWidgetsMap = useMemo(() => {
		const map: StatelessWidgetsMap = {};
		const widgets = getWidgetsMap();
		// paintedPortsRef.current = {
		// 	widgets: {},
		// 	visibleGroup,
		// };

		for (const gateId in widgets) {
			if (visibleGroup && visibleGroup.groupId === widgets[gateId].groupId) {
				// Show only widgets inside the current group
				map[gateId] = { ...widgets[gateId] };
				// paintedPortsRef.current.widgets[gateId] = false;
			} else if (!visibleGroup && !widgets[gateId].groupId) {
				// Show only widgets without a group
				map[gateId] = { ...widgets[gateId] };
				// paintedPortsRef.current.widgets[gateId] = false;
			}

			// Build a map 
		}

		// Keep reference updated every time it changes.
		// gatesRef.current = map;
		return map;
	}, [visibleGroup, totalWidgets]);


	const previouslyDrawnWidgetsRef = useRef<{widgets: StatelessWidgetsMap, groupInfo: FolderPathInfo | null}>({ widgets: gatesToShow, groupInfo: visibleGroup });


	// Decodes the event and dispatches the action
	const addOrRemoveChild = useCallback((action: 'add' | 'remove', event: ConnectionEstablishedParams | ConnectionMovedParams) => {

		const isMovedEvent = !!(event as ConnectionMovedParams).originalSourceId && !!(event as ConnectionMovedParams).originalTargetId;

		const sourceId = (event as ConnectionMovedParams).originalSourceId || (event as ConnectionEstablishedParams).sourceId;
		const targetId = (event as ConnectionMovedParams).originalTargetId || (event as ConnectionEstablishedParams).targetId;

		let sourceEndpointId = isMovedEvent ? (event as ConnectionMovedParams).originalSourceEndpoint.getUuid() : (event as ConnectionEstablishedParams).sourceEndpoint.getUuid();
		let targetEndpointId = isMovedEvent ? (event as ConnectionMovedParams).originalTargetEndpoint.getUuid() : (event as ConnectionEstablishedParams).targetEndpoint.getUuid();

		// Now, remove the gateId which is not relevant
		const decodedSource = decodeWidgetPortIdentifier(sourceEndpointId);
		const decodedTarget = decodeWidgetPortIdentifier(targetEndpointId);

		sourceEndpointId = createWidgetPortIdentifier(decodedSource.widgetId, decodedSource.portType, decodedSource.portName);
		targetEndpointId = createWidgetPortIdentifier(decodedTarget.widgetId, decodedTarget.portType, decodedTarget.portName);

		const sourceGate = decodeDomId(sourceId);
		const targetGate = decodeDomId(targetId);

		if (!sourceGate || !targetGate) { return; }

		const executeFun = action === 'add' ? addChildGate : removeChildGate;


		dispatch(executeFun({
			recipeId: recipeId,
			blockRecipeId: thingRecipeId,
			sourceGateId: sourceGate.gateId!,
			targetGateId: targetGate.gateId!,
			sourcePortId: sourceEndpointId,
			targetPortId: targetEndpointId
		}));
	}, [dispatch, thingRecipeId, recipeId]);


	/** removes the type-N class added to the canvas container */
	const removeContainerClass = useCallback((conn: Connection) => {
		// Remove container reference
		const types = getSourceEndpointNames(conn);
		if (types) {
			setCanvasContainerClass(types, true);
		}
	}, []);

	/** Invoked when 2 gates are linked */
	const handleConnectionEvent = useCallback((event: ConnectionEstablishedParams, mouseEvent: MouseEvent) => {
		const manuallyAdded = mouseEvent !== undefined;
		const sourceGate = decodeDomId(event.sourceId);
		if (sourceGate?.gateId) {
			const data: KemuConnectionData = {
				sourceWidgetId: sourceGate.gateId,
				sourcePortId: event.sourceEndpoint.getUuid(),
				targetPortId: event.targetEndpoint.getUuid()
			};

			event.connection.setData(data);
		}

		// Ignore any programmatic links
		if (manuallyAdded) {
			addOrRemoveChild('add', event);
			removeContainerClass(event.connection);
		}
	}, [addOrRemoveChild, removeContainerClass]);

	/** Invoked when 2 gates are unlinked */
	const handleConnectionDetachedEvent = useCallback((event: ConnectionEstablishedParams, mouseEvent: MouseEvent) => {
		const manuallyRemoved = mouseEvent !== undefined;
		if (manuallyRemoved) {
			addOrRemoveChild('remove', event);
			removeContainerClass(event.connection);
		}
	}, [addOrRemoveChild, removeContainerClass]);

	/** Invoked when a connections is moved from one gate to another */
	const handleConnectionMovedEvent = useCallback((event: ConnectionMovedParams, mouseEvent: MouseEvent) => {
		const sourceBlock = decodeDomId(event.originalSourceId);
		const targetBlock = decodeDomId(event.originalTargetId);
		if (!sourceBlock || !targetBlock) { return; }
		logger.log(`Widget connection moved event from ${sourceBlock.blockId} to ${targetBlock.blockId} by ${mouseEvent ? 'user' : 'system'}`);
		addOrRemoveChild('remove', event);
	}, [addOrRemoveChild]);


	/** 
	 * Updates the block's position in the recipe
	 **/
	const handleGateMovedEvent = useCallback((evt: PlumbDragEvent) => {
		const gate = decodeDomId(evt.el.id);
		// Update Jul/17: Ignore virtual ports (which up until now are the only ones using uid)
		// since dragging them around should NOT affect the position of the actual widget.
		if (gate && !gate.uid) {
			const gateId = gate.gateId!;
			const canvasInfo = getWidgetCanvasInfo(recipeId, thingRecipeId, gateId);
			canvasInfo.position = {
				x: evt.pos.left,
				y: evt.pos.top
			};

			dispatch(setGatePosition({
				canvasInfo: canvasInfo,
				sourceGateId: gateId,
				blockRecipeId: thingRecipeId,
				recipeId
			}));

			// If this was a locked note drag, clear drag selection
			// Check if the widget that finished dragging is a locked note
			const widgetsMap = getWidgetsMap();
			const widgetInfo = widgetsMap[gateId];
			if (widgetInfo?.type === WidgetType.note) {
				const noteState = getWidgetState<NoteWidgetState>(gateId, thingRecipeId, recipeId);
				if (noteState?.locked) {
					// Clear jsPlumb's drag selection when the locked note finishes dragging
					plumbInstance?.clearDragSelection();
				}
			}
		}
	}, [recipeId, thingRecipeId, dispatch, plumbInstance, getWidgetsMap]);


	/** Triggered when a new connection is being made */
	const handleConnectionDragEvent = useCallback((connEvt: Connection) => {
		const types = getSourceEndpointNames(connEvt);
		if (types) {
			setCanvasContainerClass(types);
		}
	}, []);

	/** New connection was aborted */
	const connectionAborted = useCallback((connEvt: Connection) => {
		const types = getSourceEndpointNames(connEvt);
		if (types) {
			setCanvasContainerClass(types, true);
		}
	}, []);

	/** triggered when the user double clicks on a connection */
	const handleConnectionDblClick = useCallback((connEvt: Connection, originalEvent: Event) => {
		// Note: this triggers the handleConnectionDetachedEvent. We pass the original event so
		// it gets recognized as a manual event and the relationship removed from the recipe
		plumbInstance?.deleteConnection(connEvt, { fireEvent: true, originalEvent });
	}, [plumbInstance]);

	/** Called when a connection is about to be dropped */
	const handleBeforeDrop = useCallback((event: JsPlumbBeforeDropEvent) => {
		// Check if the target is not already a children
		const sourceGate = decodeDomId(event.sourceId);
		const targetGate = decodeDomId(event.targetId);
		if (!sourceGate || !targetGate || !targetGate.gateId || !sourceGate.gateId) { return; }
		const sourceEp = event.connection.endpoints[0];

		// NOTE: Because the useSelector that extracts the current gates from the store
		// is removing the 'children' property in order to avoid re-renderings, we don't actually
		// have an up-to-date state of the gates (in the `gates` variable declared at the beginning of the component).
		// So, we use the utility module to access the recipe cache directly and check if the gate is already a child.
		const alreadyAChild = isGateAChildAtPort(
			sourceGate.recipeId,
			sourceGate.blockId,
			sourceGate.gateId,
			targetGate.gateId,
			sourceEp.getUuid(),
			// Why using `event.dropEndpoint.getUuid`?
			// From the official documentation: https://docs.jsplumbtoolkit.com/community/current/articles/events-community.html#evt-beforedrop
			// "You can access the 'endpoints' array in a Connection to get the Endpoints involved in the Connection, 
			// but be aware that when a Connection is being dragged, one of these Endpoints will always be a transient 
			// Endpoint that exists only for the life of the drag. To get the Endpoint on which the Connection is being 
			// dropped, use the dropEndpoint member"
			event.dropEndpoint.getUuid()
		);

		if (alreadyAChild) {
			logger.log('Ignoring connection request, child widget and port already exist');
			return false;
		}

		return true;
	}, []);


	const drawAllPortsAndConnections = useCallback(() => {
		setDrawingStatus('drawing');
		plumbInstance?.batch(() => {
			// Add the visible group to the widgets map so the group connections and ports are also drawn
			const widgetsWithGroup: StatelessWidgetsMap = { ...gatesToShow };
			const widgets = getWidgetsMap();
			if (visibleGroup && !widgetsWithGroup[visibleGroup.groupId]) {
				const groupWidget = widgets[visibleGroup.groupId];
				widgetsWithGroup[visibleGroup.groupId] = groupWidget;
			}


			drawPortsAndConnections(widgetsWithGroup);
		});
		setDrawingStatus('drawn');
		// console.log('🔥 all ports and connections drawn');

	}, [plumbInstance, gatesToShow, visibleGroup, drawPortsAndConnections, setDrawingStatus]);

	const removeAllPortsAndConnections = useCallback(() => {
		setDrawingStatus('removing');
		plumbInstance?.batch(() => {
			const widgets = getWidgetsMap();
			// Remove virtual ports
			cleanupAllVirtualPorts();
			removePortsAndConnections(widgets);
		});
		setDrawingStatus('removed');
	}, [plumbInstance, removePortsAndConnections, cleanupAllVirtualPorts, setDrawingStatus]);


	// IMPORTANT: Order of these `useCallbacks` and `useEffects` matters!
	// 1) Remove event listeners MUST happen BEFORE we update the gatesRef, otherwise
	// the reference inside removeEventListeners will be too new and old connections won't be removed.
	const removeEventListeners = useCallback(() => {
		if (!plumbInstance) { return; }

		logger.log('Removing event listeners');
		plumbInstance.unbind('connectionDrag', handleConnectionDragEvent);
		plumbInstance.unbind('connectionAborted', connectionAborted);
		plumbInstance.unbind('connection', handleConnectionEvent);
		plumbInstance.unbind('connectionDetached', handleConnectionDetachedEvent);
		plumbInstance.unbind('connectionMoved', handleConnectionMovedEvent);
		plumbInstance.unbind('beforeDrop', handleBeforeDrop);
		plumbInstance.unbind('drag:stop', handleGateMovedEvent);
		plumbInstance.unbind('dblclick', handleConnectionDblClick);
		// disconnectGates(visibleGroup, recipeId, thingRecipeId, plumbInstance, gatesRef.current);
	}, [
		plumbInstance, recipeId, thingRecipeId, visibleGroup,
		handleBeforeDrop, handleConnectionMovedEvent, handleConnectionEvent,
		handleGateMovedEvent, handleConnectionDetachedEvent, handleConnectionDragEvent,
		connectionAborted, handleConnectionDblClick
	]);

	// 2) We need to keep the reference updated so that `setupListeners` and `removeEventListeners` use the
	// latest version, since useMountEffect will call them with a stale version of 'gates'
	// useEffect(() => {
	// 	gatesRef.current = gatesToShow;
	// }, [gatesToShow, recipeId, thingRecipeId]);


	// 3) We need to define this AFTER the gatesRef has been updated, so that
	// when building the connections, gatesRef refers to the newest copy
	const setupListeners = useCallback(() => {
		if (!plumbInstance) { return; }

		logger.log('Creating event listeners');
		plumbInstance.bind('connectionDrag', handleConnectionDragEvent);
		plumbInstance.bind('connectionAborted', connectionAborted);
		plumbInstance.bind('connection', handleConnectionEvent);
		plumbInstance.bind('connectionDetached', handleConnectionDetachedEvent);
		plumbInstance.bind('connectionMoved', handleConnectionMovedEvent);
		plumbInstance.bind('beforeDrop', handleBeforeDrop);
		plumbInstance.bind('drag:stop', handleGateMovedEvent);
		plumbInstance.bind('dblclick', handleConnectionDblClick);
	}, [
		plumbInstance, /* visibleGroup, */ handleBeforeDrop,
		handleConnectionMovedEvent, handleConnectionEvent,
		handleGateMovedEvent, handleConnectionDetachedEvent,
		handleConnectionDragEvent, connectionAborted,
		handleConnectionDblClick,
	]);

	// Force repainting everything if the group changes
	useEffect(() => {
		console.log('Setting up listeners');
		setupListeners();
		// drawAllPortsAndConnections();
		return () => {
			console.log('Removing event listeners');
			removeEventListeners();
			// removeAllPortsAndConnections();
		};
	}, [
		visibleGroup,
		setupListeners,
		removeEventListeners,
		// removeAllPortsAndConnections,
		// forceReRender,
	]);

	useEffect(() => {
		// console.log('✅Drawing all ports and connections');
		// setTimeout(() => {
			drawAllPortsAndConnections();
		// }, 1000);
		return () => {
			// console.log('❌Removing all ports and connections');
			removeAllPortsAndConnections();
			// removeVirtualPortsForCurrentGroup();
		};
	}, [visibleGroup, drawAllPortsAndConnections, removeAllPortsAndConnections, forceReRender]);


	/**
	 * Used to detect removed widgets and remove their ports and connections
	 */
	useEffect(() => {
		if (previouslyDrawnWidgetsRef.current.groupInfo?.groupId !== visibleGroup?.groupId) {
			previouslyDrawnWidgetsRef.current = { widgets: gatesToShow, groupInfo: visibleGroup };
			return;
		}

		const gatesToShowKeys = Object.keys(gatesToShow);
		const previouslyDrawnWidgetsKeys = Object.keys(previouslyDrawnWidgetsRef.current.widgets);
		const changed = gatesToShowKeys.length !== previouslyDrawnWidgetsKeys.length || gatesToShowKeys.some(widgetId => !previouslyDrawnWidgetsKeys.includes(widgetId));

		if (changed) {
			const removedWidgets = previouslyDrawnWidgetsKeys.reduce((acc, widgetId) => {
				if (!gatesToShowKeys.includes(widgetId)) {
					acc[widgetId] = previouslyDrawnWidgetsRef.current.widgets[widgetId];
				}
				return acc;
			}, {} as StatelessWidgetsMap);

			removePortsAndConnections(removedWidgets);
		}

		previouslyDrawnWidgetsRef.current = { widgets: gatesToShow, groupInfo: visibleGroup };
	}, [gatesToShow, removePortsAndConnections, visibleGroup]);

	// Clear jsPlumb's drag selection when widgets are grouped
	// This ensures that dragging the group widget doesn't affect the inner widgets
	useEffect(() => {
		const hadSelection = previousSelectedWidgetsRef.current.length > 0;
		const hasSelection = selectedWidgets.length > 0;

		// If selection went from non-empty to empty, clear jsPlumb's drag selection
		if (hadSelection && !hasSelection && plumbInstance) {
			plumbInstance.clearDragSelection();
		}

		previousSelectedWidgetsRef.current = selectedWidgets;
	}, [selectedWidgets, plumbInstance]);


	if (!plumbInstance) { return null ; }

	return (
		// NOTE: Draggable objects can NOT be contained, they must be immediate children of the 
		// jsplumb draggable container, currently set to 'canvas-background' (CANVAS_DRAGGABLE_CLASS). Check canvas.tsx for more info
		<>
			{/* {logicMapperSettings.debugPanelVisible && (
				// <WidgetPortInfoPanel plumbInstance={plumbInstance} />
				<WidgetPortInspector />
			)} */}

			{/* {Object.keys(gatesToShow).map((gateName) => { */}
			{Object.keys(widgetsInRecipe).map((widgetId) => {
				const widget = gatesToShow[widgetId];
				const visible = !!widget;
				// Always render hub services even if they are not visible
				if (!visible && widgetsInRecipe[widgetId].type !== WidgetType.hubService) { return; }

				return (
					<CanvasGateWrapper
						// onPortsPainted={handlePortsPainted}
						hidden={!visible}
						gateId={widgetsInRecipe[widgetId].id}
						key={widgetsInRecipe[widgetId].id}
						thingRecipeId={thingRecipeId}
						thingDbId={thingDbId}
						thingVersion={thingVersion}
						recipeId={recipeId}
					/>
				);
			})}


			{!!visibleGroup && (
				<VirtualPortManager
					recipeId={recipeId}
					blockId={thingRecipeId}
					groupId={visibleGroup.groupId}
					recipeType={currentRecipeType}
					// IMPORTANT: force react to render a new instance of the component when the group changes
					// otherwise the useEffect of this parent will be triggered BEFORE the VirtualPort components have
					// a chance to create the DOM elements.
					key={visibleGroup.groupId}
				/>
			)}
		</>
	);
};

export default LogicMapper;
